import {useMemo} from 'react';

import {CurrentOrders, Order, OrderService} from '@domain/model/Order';
import {
  ORDER_DRAFT_STATUSES,
  ORDER_IN_PROGRESS_STATUSES,
  ORDER_RETURNING_STATUS,
} from '@views/Account/Account.definition';
import {useOverallInfo} from '@components/_context';
import {CheckoutProjection} from '@lookiero/checkout/dist/src/projection/checkout/checkout';

export const getCurrentOrders = (orders: Order[], checkout?: CheckoutProjection): CurrentOrders => {
  if (!orders || !orders.length) return {};

  const findOrder = (orders: Order[], statuses: string[]): OrderService | undefined => {
    const order = orders.find(({services: {0: order}}) => order.status && statuses.includes(order.status));
    return order && order.services && order.services[0];
  };

  const returning = findOrder(orders, ORDER_RETURNING_STATUS);
  // The delivered box no longer carries a dedicated legacy status (ENTREGADO removed): it is identified
  // by joining on `boxId` against the customer's first available checkout (UUID === UUID).
  // The checkout-joined (delivered) order must WIN over a status-matching order: in a multi-order state
  // `find` would otherwise return the first status match and render the wrong box for the checkout view.
  const deliveredByCheckout =
    checkout?.boxId != null
      ? orders.find(({services: {0: order}}) => order.boxId === checkout.boxId)?.services?.[0]
      : undefined;
  const inProgressByStatus = findOrder(orders, ORDER_IN_PROGRESS_STATUSES);
  const inProgress = deliveredByCheckout ?? inProgressByStatus;
  const draft = findOrder(orders, ORDER_DRAFT_STATUSES);

  return {returning, inProgress, draft};
};

export const useCurrentOrders = (): CurrentOrders => {
  const {orders = [], checkout} = useOverallInfo();
  return useMemo(() => getCurrentOrders(orders, checkout), [orders, checkout]);
};
